// LIBRERIE/IMPORTS
import React from "react";
import { Helmet } from 'react-helmet';
import { useTranslation } from "react-i18next";

// HOOKS
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";

// CONFIGURAZIONI
import { AGENCY_ID, appConfig } from "../../config/appConfig";

// COMPONENTI
import SubHeaderHome from "../Header/subHeader/SubHeaderHome";
import SubHeaderDefault from "../Header/subHeader/SubHeaderDefault";
import SubHeaderImmobili from "../Header/subHeader/SubHeaderImmobili";
import PropertySearchField from "./Search";
import ServicesSection from "./ServicesSection";
import Vetrina1 from "./Vetrina1";
import Vetrina2 from "./Vetrina2";
import FooterBannerVendi from "./FooterBannerVendi";
import LocationSearch from "./LocationSearch";
import Presentation from "./Presentation";
import SocialPost from "./SocialPost";
import GoogleReviews from '../Reviews/GoogleReviews';
import HomeValuation from "./HomeValuation/HomeValuation";
//import LuxurySection from "./LuxurySection";
import LastArticles from "./LastArticles";
import BannerVendi from "./BannerVendi";

interface Props {
  seo: boolean;
  vetrina2: boolean;
  pageType: string;
  filters: { [key: string]: string[] };
  showServices: boolean;
  showLocationSearch: boolean;
  showFooterBannerVendi: boolean;
  showPresentationSection: boolean;
  showSocialSection: boolean;
  showVetrina1: boolean;
}

const HomePage: React.FC<Props> = ({
  seo,
  vetrina2,
  pageType,
  filters,
  showServices,
  showLocationSearch,
  showFooterBannerVendi,
  showPresentationSection,
  showSocialSection,
  showVetrina1
}) => {

  const { t } = useTranslation();
  const { agency } = useGetAgencyDetails(AGENCY_ID);

  // --- Lettura Flag Indipendenti dalla Configurazione ---
  const shouldShowInSubHeader = appConfig.ui.searchForm.showSearchInHomePageSubHeader;
  const shouldShowBelowIntro = appConfig.ui.searchForm.showSearchBelowHomePageIntro;
  const shouldShowSearchAndFiltersSection = appConfig.ui.homePage.showSearchAndFilters;
  const homeValuationConfig = appConfig.features.homeValuation;
  // --- Fine Lettura ---

  const isSeo = pageType === 'home';

  return (
    <>
      <Helmet>
        <title>{t('homePage.metaTitle')}</title>
        <meta name="description" content={t('homePage.metaDescription') as string} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@immobiliarececcarelli" />
        <meta name="twitter:creator" content="@gruppoinsieme" />
        <meta property="twitter:url" content="https://www.immobiliarececcarelli.it/it" />
        <meta property="twitter:title" content="Agenzia Immobiliare a Massa e Ronchi | Vendite, Affitti e Valutazioni | Ceccarelli" />
        <meta property="twitter:description" content="Agenzia immobiliare a Massa. Vendita, affitti e valutazioni professionali. Scopri le migliori case tra Massa, Ronchi e Marina di Massa." />
        <meta property="twitter:image" content="https://www.immobiliarececcarelli.it/assets/images/logo.png" />
        <meta property="og:title" content="Agenzia Immobiliare a Massa e Ronchi | Vendite, Affitti e Valutazioni | Ceccarelli" />
        <meta property="og:description" content="Agenzia immobiliare a Massa. Vendita, affitti e valutazioni professionali. Scopri le migliori case tra Massa, Ronchi e Marina di Massa."/>

        <meta property="og:url" content="https://www.immobiliarececcarelli.it/it" />
        <meta property="og:description" content="Agenzia immobiliare a Massa. Vendita, affitti e valutazioni professionali. Scopri le migliori case tra Massa, Ronchi e Marina di Massa."/>
        <meta property="og:url" content="https://www.immobiliarececcarelli.it/it"/>
        
      </Helmet>

      {/* === Rendering Condizionale Sub Headers === */}
      {pageType === "home" && (
        <SubHeaderHome
          isSeo={isSeo}
          showSearchForm={shouldShowInSubHeader}
        />
      )}
      {pageType === "immobili" && (
        <SubHeaderImmobili
          bodyTitle={t('propertyList.pageTitle')}
          filters={filters}
        />
      )}
      {pageType === "default" && (
        <SubHeaderDefault
          title={t('homePage.subHeaderDefaultTitle')}
          isSeo={isSeo}
        />
      )}

      {shouldShowSearchAndFiltersSection && pageType === 'home' &&
        <section id="search-box" className="space-ptb position-relative">
          <div className="container-fluid w-90">
            <div className="row">
              <div className="col-12">
                <div className="mb-4">
                  <h3 className="asH2SubTitle">{t('homePage.searchFilterSection.title')}</h3>
                </div>
              </div>
            </div>
            {shouldShowBelowIntro && <PropertySearchField searchData={{}} />}
          </div>
        </section>
      }

      {showPresentationSection && (
        <Presentation />)
      }

      {/* Vetrina con immobili */}
      {showVetrina1 && <Vetrina1 />}

      {/* Vetrina con immobili */}
      {vetrina2 && <Vetrina2 agencyName={agency?.agencyName ?? ''} />}

      {/* Sezione Luxury */}
      {/*<LuxurySection />*/}

      {/* Banner Valuta */}
      <BannerVendi />

      <LastArticles />

      {/* Sezione Google Reviews */}
      {appConfig.features.googleReviews.enabled && (
        <section id="reviews-section" className="space-ptb mb-3">
          <div className="container-fluid">
            <GoogleReviews />
          </div>
        </section>
      )}

      {homeValuationConfig.enabled && homeValuationConfig.displayMode === 'banner' && (
        <section className="space-ptb bg-light">
          <div className="container">
            <HomeValuation />
          </div>
        </section>
      )}

      {/* Ricerca veloce con immagini */}
      {showLocationSearch && <LocationSearch seo={seo} />}

      <ServicesSection showServices={showServices} />

      {/* Footer Banner Vendi */}
      {showFooterBannerVendi && <FooterBannerVendi />}

      {/* Social section */}
      {showSocialSection && <SocialPost />}
    </>
  );
};

export default HomePage;